import styles from './SidePanelFolder.module.css';
import formStyles from '@he-novation/design-system/styles/form-styles.module.css';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PROJECT_STATUS } from '@he-novation/config/constants/projects.constants';
import { RECURSIVE_SETTINGS } from '@he-novation/config/paths/modals.constants';
import { FullFolder } from '@he-novation/config/types/folder.types';
import { FolderUpdateBody } from '@he-novation/config/types/payloads/folder.payload';
import { WatermarkSchema } from '@he-novation/config/types/watermark.types';
import { WorkspaceFeature } from '@he-novation/config/types/workspace.types';
import { acl, featureACL } from '@he-novation/config/utils/acl';
import { Loader } from '@he-novation/design-system/components/widgets/Loader/Loader';
import { Tabs } from '@he-novation/design-system/components/widgets/Tabs/Tabs';
import { asyncFolderUpdate, fetchFolderSize } from '@he-novation/front-shared/async/folder.async';
import cn from 'classnames';
import { useAtomValue } from 'jotai';
import { SidePanelFolderInfo } from './SidePanelFolderInfo/SidePanelFolderInfo';
import { SidePanelFolderSettings } from './SidePanelFolderSettings/SidePanelFolderSettings';

import { workspaceFeaturesAtom, workspaceNameAtom } from '$atoms/workspace-atoms';
import { ModalRecurseSettingsProps } from '$components/modal/modals/ModalRecurseSettings/ModalRecurseSettings';
import { useModal } from '$hooks/useModal';
import { PubSubEvent, useSubscribe } from '$hooks/useSubscribe';
import { useTranslate } from '$hooks/useTranslate';
import { currentProjectSelector } from '$redux/content/projects/projectsSelector';
import { updateUserPreferences } from '$redux/user/userActions';
import { currentUserUuidSelector, preferencesSelector } from '$redux/user/userSelectors';

export type FormValues = {
    [field: string]: any;
};

type SidePanelFolderProps = {
    folder: FullFolder;
    readonly?: boolean;
};

export function SidePanelFolder({ folder }: SidePanelFolderProps) {
    const dispatch = useDispatch();

    const { openModal } = useModal();

    const workspaceName = useAtomValue(workspaceNameAtom);
    const workspaceFeatures = useAtomValue(workspaceFeaturesAtom);

    const { currentUserUuid } = useSelector(currentUserUuidSelector);
    const { currentProject } = useSelector(currentProjectSelector());
    const { preferences } = useSelector(preferencesSelector);

    const [folderSize, setFolderSize] = useState<number>();
    const [labels, setLabels] = useState<string[]>([]);
    const [tags, setTags] = useState<string[]>([]);

    useEffect(() => {
        if (folder) {
            fetchFolderSize(workspaceName, folder.uuid).then(({ size }) => {
                setFolderSize(size);
            });
            setLabels(folder.labels);
            if ('tags' in folder) setTags(folder.tags);
        }
    }, [folder]);

    useSubscribe(
        PubSubEvent.FileUpload,
        () => {
            if (folder) {
                fetchFolderSize(workspaceName, folder.uuid).then(({ size }) => {
                    setFolderSize(size);
                });
            }
        },
        [folder]
    );

    const onInfoChange = useCallback(
        async ({ tags, labels, description }) => {
            if (!folder) return;

            if (tags) {
                setTags(tags);
            }

            if (labels) {
                setLabels(labels);
            }

            if (acl.folders.modify(folder.role)) {
                await asyncFolderUpdate(workspaceName, folder.uuid, {
                    tags,
                    labels,
                    metadata: { notes: description }
                } as FolderUpdateBody);
            }

            const userTags = preferences.tags || [];
            const userLabels = preferences.labels || [];

            dispatch(
                updateUserPreferences(currentUserUuid, {
                    tags: tags ? [...new Set([...userTags, ...tags])] : userTags,
                    labels: labels ? [...new Set([...userLabels, ...labels])] : userLabels
                })
            );
        },
        [folder]
    );

    const onSettingsChange = useCallback(
        async (settings: FormValues, watermark?: WatermarkSchema) => {
            if (!folder) return;

            if (acl.folders.modify(folder.role)) {
                await asyncFolderUpdate(
                    workspaceName,
                    folder.uuid,
                    dataToSettings({ workspaceFeatures, data: settings, watermark })
                );
            }
        },
        [folder]
    );

    const onRecursiveButtonClick = useCallback(
        (settings: FormValues, watermark?: WatermarkSchema) => {
            if (!folder) return;

            openModal(
                RECURSIVE_SETTINGS,
                {
                    uuid: folder.uuid,
                    settings: dataToSettings({
                        workspaceFeatures,
                        data: settings,
                        tags,
                        labels,
                        watermark
                    })
                } satisfies ModalRecurseSettingsProps['payload'],
                null,
                true
            );
        },
        [folder]
    );

    const { t } = useTranslate();

    return (
        <div
            id="c-side-panel-folder-settings"
            className={cn(styles.folderSidePanel, formStyles.dark)}
        >
            {folder && (
                <Tabs
                    className={styles.tabs}
                    tabs={[
                        {
                            id: 'side-panel-tab-folder-info',
                            label: t('folder.Information'),
                            icon: 'info'
                        },
                        {
                            id: 'side-panel-tab-folder-settings',
                            label: t('common.Settings'),
                            icon: 'cog'
                        }
                    ]}
                >
                    {folderSize !== undefined ? (
                        <SidePanelFolderInfo
                            folder={folder}
                            folderSize={folderSize}
                            onChange={onInfoChange}
                            readonly={currentProject?.status === PROJECT_STATUS.ARCHIVED}
                        />
                    ) : (
                        <Loader />
                    )}
                    <SidePanelFolderSettings
                        folder={folder}
                        onChange={onSettingsChange}
                        onRecursiveButtonClick={onRecursiveButtonClick}
                        readonly={currentProject?.status === PROJECT_STATUS.ARCHIVED}
                    />
                </Tabs>
            )}
        </div>
    );
}

function dataToSettings({
    workspaceFeatures,
    data,
    tags,
    labels,
    watermark
}: {
    workspaceFeatures: WorkspaceFeature[];
    data: FormValues;
    tags?: string[];
    labels?: string[];
    watermark?: WatermarkSchema;
}): FolderUpdateBody {
    const payload: FolderUpdateBody = {
        admittance: data.admittance,
        default_presets: data.bypass,
        export_mode: data.export ? data.export_mode : 'never',
        encrypted: data.encrypted,
        public: data.public,
        tags: tags?.filter((t) => t),
        labels: labels?.filter((l) => l),
        public_download: data['folder-option-download-public'],
        public_password: data['folder-settings-form-public-password'] || '',
        handlesHtmlBundles: data['trigger-bundles'],
        watermark: featureACL.watermark(workspaceFeatures) ? watermark : undefined
    };

    payload.metadata = {
        admittance: data.admittance && {
            role: data['admittance-role'],
            download: data['admittance-download'],
            export: data['admittance-export']
        },
        notes: data.description
    };

    return payload;
}
